import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authButtonSmallStyle, authButtonStyle } from '../constants/authStyles';
import { tl } from '../utils/inlineI18n';
import { ItemImage } from './inventory/inventoryBagGridUtils';
import { getActiveLocale } from '../utils/localeFormat';

export const getPendingBaseItemId = (item) => (
  item?.weaponId ||
  item?.armorId ||
  item?.helmetId ||
  item?.bootId ||
  item?.earringId ||
  item?.necklaceId ||
  item?.shieldId ||
  item?.potId ||
  item?.otherId
);

export const enrichPendingItems = (pendingItems, catalog = {}) => (
  (pendingItems || []).map((pendingItem) => {
    const baseItemId = getPendingBaseItemId(pendingItem.item);
    const catalogItems = catalog[pendingItem.inventoryType] || [];
    const baseItem = catalogItems.find((item) => Number(item.id) === Number(baseItemId)) || null;
    return {
      ...pendingItem,
      baseItem,
      count: pendingItem.item?.count || 1
    };
  }).filter((pendingItem) => pendingItem.item && pendingItem.baseItem)
);

const PendingInventoryPanel = ({
  pendingItems,
  catalog,
  loading,
  actionLoading,
  onClaim,
  onSell,
  onDiscard,
  onPendingDragStart,
  onPendingDragEnd,
  onPendingTouchStart,
  onPendingPressStart,
  onPendingPressEnd,
  canSmeltOre,
  smeltInputCount = 10,
  onSmeltOre,
  oreSmeltLoading = false,
  gifts = [],
  onClaimGift
}) => {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const scrollRef = useRef(null);
  const pendingSkipClickRef = useRef(false);
  const pendingScrollLockedRef = useRef(false);
  const [activePendingItem, setActivePendingItem] = useState(null);
  const [isQueueScrollLocked, setIsQueueScrollLocked] = useState(false);

  const enrichedItems = useMemo(
    () => enrichPendingItems(pendingItems, catalog),
    [catalog, pendingItems]
  );

  const scrollQueue = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction * Math.max(180, scrollRef.current.clientWidth * 0.7),
      behavior: 'smooth'
    });
  };

  const lockPendingQueueScroll = () => {
    if (pendingScrollLockedRef.current) return;
    pendingScrollLockedRef.current = true;
    setIsQueueScrollLocked(true);
    onPendingPressStart?.();
  };

  const unlockPendingQueueScroll = () => {
    if (!pendingScrollLockedRef.current) return;
    pendingScrollLockedRef.current = false;
    setIsQueueScrollLocked(false);
    onPendingPressEnd?.();
  };

  const handlePendingPointerDown = (event, pendingItem) => {
    if (actionLoading || !pendingItem?.baseItem) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    lockPendingQueueScroll();
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePendingPointerEnd = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    unlockPendingQueueScroll();
  };

  const handlePendingDragStart = (event, pendingItem) => {
    if (actionLoading || !pendingItem?.baseItem) return;
    pendingSkipClickRef.current = true;
    lockPendingQueueScroll();
    onPendingDragStart?.(event, pendingItem);
  };

  const handlePendingDragEnd = () => {
    pendingSkipClickRef.current = false;
    unlockPendingQueueScroll();
    onPendingDragEnd?.();
  };

  const handlePendingTouchStart = (event, pendingItem) => {
    if (actionLoading || !pendingItem?.baseItem) return;
    lockPendingQueueScroll();
    onPendingTouchStart?.(event, pendingItem);
  };

  const handlePendingCardClick = (pendingItem) => {
    if (pendingSkipClickRef.current) {
      pendingSkipClickRef.current = false;
      return;
    }
    setActivePendingItem(pendingItem);
  };

  return (
    <div className="pending-inventory-panel">
      <div className="pending-inventory-header">
        <div>
          <h2>{t('pendingTitle')}</h2>
          <p>{t('pendingSubtitle')}</p>
        </div>
        <div className="pending-inventory-count">{t('pendingItemCount', { count: enrichedItems.length })}</div>
      </div>

      {gifts.length > 0 && (
        <div className="pending-gifts">
          <h3>{tl('Arkadaşlardan gelenler', 'From friends')}</h3>
          {gifts.map((gift) => (
            <div key={gift.giftId} className="pending-gift-row">
              <span className="pending-gift-icon">
                {gift.kind === 'gold' ? '💰' : gift.imageUrl ? (
                  <ItemImage imageUrl={gift.imageUrl} alt={gift.name || ''} />
                ) : '🎁'}
              </span>
              <span className="pending-gift-text">
                <strong>
                  {gift.kind === 'gold'
                    ? `${Number(gift.amount || 0).toLocaleString(getActiveLocale())} ${tl('yang', 'yang')}`
                    : `${gift.name || tc('unknownItem')}${gift.count > 1 ? ` x${gift.count}` : ''}`}
                </strong>
                <small>{tl('Gönderen', 'From')}: {gift.fromUsername}</small>
              </span>
              <button
                type="button"
                className="auth-button auth-button--compact"
                style={authButtonSmallStyle}
                disabled={actionLoading}
                onClick={() => onClaimGift?.(gift)}
              >
                {t('pendingClaim')}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="pending-queue-shell">
        <button
          type="button"
          className="auth-button auth-button--compact pending-queue-nav"
          style={authButtonSmallStyle}
          onClick={() => scrollQueue(-1)}
          aria-label={t('pendingScrollLeft')}
        >
          ←
        </button>

        <div
          className={`pending-queue-scroll${isQueueScrollLocked ? ' pending-queue-scroll--locked' : ''}`}
          ref={scrollRef}
        >
          {loading ? (
            <div className="pending-queue-empty">{t('pendingLoading')}</div>
          ) : enrichedItems.length === 0 ? (
            <div className="pending-queue-empty">{t('pendingEmpty')}</div>
          ) : (
            enrichedItems.map((pendingItem, index) => (
              <div
                role="button"
                tabIndex={0}
                key={pendingItem.pendingId}
                className="pending-queue-card pending-queue-card--draggable"
                draggable={!actionLoading}
                onPointerDown={(event) => handlePendingPointerDown(event, pendingItem)}
                onPointerUp={handlePendingPointerEnd}
                onPointerCancel={handlePendingPointerEnd}
                onDragStart={(event) => handlePendingDragStart(event, pendingItem)}
                onTouchStart={(event) => handlePendingTouchStart(event, pendingItem)}
                onDragEnd={handlePendingDragEnd}
                onClick={() => handlePendingCardClick(pendingItem)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handlePendingCardClick(pendingItem);
                  }
                }}
              >
                <span className="pending-queue-order">#{index + 1}</span>
                <span className="pending-queue-image">
                  {pendingItem.baseItem?.imageUrl ? (
                    <ItemImage imageUrl={pendingItem.baseItem.imageUrl} alt={pendingItem.baseItem.name} />
                  ) : (
                    <span className="slot-placeholder-icon">📦</span>
                  )}
                </span>
                <span className="pending-queue-name">{pendingItem.baseItem?.name || tc('unknownItem')}</span>
                {pendingItem.fromUsername && (
                  <span className="pending-queue-from">🎁 {pendingItem.fromUsername}</span>
                )}
                {pendingItem.count > 1 && (
                  <span className="pending-queue-count">x{pendingItem.count}</span>
                )}
              </div>
            ))
          )}
        </div>

        <button
          type="button"
          className="auth-button auth-button--compact pending-queue-nav"
          style={authButtonSmallStyle}
          onClick={() => scrollQueue(1)}
          aria-label={t('pendingScrollRight')}
        >
          →
        </button>
      </div>

      {activePendingItem && (
        <div className="inventory-item-popup-backdrop" onClick={() => setActivePendingItem(null)}>
          <div className="inventory-item-popup inventory-item-popup--modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="inventory-item-popup-close"
              onClick={() => setActivePendingItem(null)}
              aria-label={tc('close')}
            >
              X
            </button>
            <h4>{activePendingItem.baseItem?.name || tc('unknownItem')}</h4>
            <p>{t('pendingQueue', { order: enrichedItems.findIndex((item) => item.pendingId === activePendingItem.pendingId) + 1 })}</p>
            <p>{tc('count')}: {activePendingItem.count}</p>
            {activePendingItem.fromUsername && (
              <p>{tl('Gönderen', 'From')}: <strong>{activePendingItem.fromUsername}</strong></p>
            )}
            {activePendingItem.baseItem?.level !== undefined && (
              <p>{tc('level')}: {activePendingItem.baseItem.level}</p>
            )}
            {activePendingItem.baseItem?.value !== undefined && (
              <p>{t('pendingValue', { amount: (Number(activePendingItem.baseItem.value) || 0) * activePendingItem.count })}</p>
            )}
            <div className="pending-popup-actions">
              {canSmeltOre?.(getPendingBaseItemId(activePendingItem.item))
                && (activePendingItem.count || 0) >= smeltInputCount && (
                <button
                  type="button"
                  className="auth-button auth-button--compact"
                  style={authButtonStyle}
                  disabled={actionLoading || oreSmeltLoading}
                  onClick={async () => {
                    const baseId = getPendingBaseItemId(activePendingItem.item);
                    if (onSmeltOre && baseId) {
                      await onSmeltOre(baseId);
                    }
                    setActivePendingItem(null);
                  }}
                >
                  {oreSmeltLoading ? '…' : t('popupSmeltOre', { count: smeltInputCount })}
                </button>
              )}
              <button
                type="button"
                className="auth-button auth-button--compact"
                style={authButtonStyle}
                disabled={actionLoading}
                onClick={async () => {
                  await onClaim(activePendingItem);
                  setActivePendingItem(null);
                }}
              >
                {t('pendingClaim')}
              </button>
              <button
                type="button"
                className="auth-button auth-button--compact"
                style={authButtonSmallStyle}
                disabled={actionLoading}
                onClick={async () => {
                  await onSell(activePendingItem);
                  setActivePendingItem(null);
                }}
              >
                {t('pendingSell')}
              </button>
              <button
                type="button"
                className="auth-button auth-button--compact pending-discard-btn"
                style={authButtonSmallStyle}
                disabled={actionLoading}
                onClick={async () => {
                  await onDiscard(activePendingItem);
                  setActivePendingItem(null);
                }}
              >
                {t('pendingDiscard')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingInventoryPanel;
